import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { makeStyles } from "tss-react/mui";
import { hideDialog } from "../../../dialog/actions";
import { IconCloseLarge } from "../../../icons/svg";
import { operatesWithEnterKey } from "../../functions.web";
import BaseDialog from "./BaseDialog";
import Button from "./Button";
import ClickableIcon from "./ClickableIcon";
const useStyles = makeStyles()((theme) => {
    return {
        header: {
            width: "100%",
            padding: "24px 24px 16px 24px",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.ui04}`,
            "@media (max-width: 768px)": {
                padding: "20px 20px 12px 20px",
            },
            "@media (max-width: 448px)": {
                padding: "16px 16px 12px 16px",
            },
        },
        title: {
            color: theme.palette.text01,
            ...theme.typography.heading5,
            margin: 0,
            padding: 0,
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: 1.4,
            "@media (max-width: 768px)": {
                fontSize: "1.1rem",
            },
            "@media (max-width: 448px)": {
                fontSize: "1rem",
            },
        },
        content: {
            height: "auto",
            overflowY: "auto",
            width: "100%",
            boxSizing: "border-box",
            padding: "24px",
            overflowX: "hidden",
            minHeight: "60px",
            maxHeight: "calc(80vh - 200px)",
            "@media (max-width: 768px)": {
                padding: "20px",
                maxHeight: "calc(70vh - 180px)",
            },
            "@media (max-width: 448px)": {
                height: "auto",
                padding: "16px",
                maxHeight: "calc(60vh - 160px)",
            },
        },
        footer: {
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "16px 24px 24px 24px",
            gap: "12px",
            borderTop: `1px solid ${theme.palette.ui04}`,
            "& button": {
                minWidth: "80px",
                padding: "10px 20px",
                fontSize: "0.9rem",
                fontWeight: 500,
                borderRadius: "8px",
                transition: "all 0.2s ease",
                "@media (max-width: 768px)": {
                    minWidth: "70px",
                    padding: "9px 16px",
                    fontSize: "0.85rem",
                },
                "@media (max-width: 448px)": {
                    flex: 1,
                    minWidth: "auto",
                    padding: "10px 12px",
                    fontSize: "0.85rem",
                },
            },
            "& button:last-child": {
                marginLeft: 0,
            },
            "@media (max-width: 768px)": {
                padding: "12px 20px 20px 20px",
                gap: "10px",
            },
            "@media (max-width: 448px)": {
                padding: "12px 16px 16px 16px",
                gap: "8px",
                flexDirection: "row",
            },
        },
    };
});
const Dialog = ({ back = { hidden: true }, cancel = { translationKey: "dialog.Cancel" }, children, className, description, disableAutoHideOnSubmit = false, disableBackdropClose, hideCloseButton = true, // پیش‌فرض: دکمه ضربدر مخفی است
disableEnter, disableEscape, ok = { translationKey: "dialog.Ok" }, onCancel, onSubmit, size, testId, title, titleKey, }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const onClose = useCallback(() => {
        dispatch(hideDialog());
        onCancel?.();
    }, [onCancel]);
    const submit = useCallback(() => {
        if ((document.activeElement && !operatesWithEnterKey(document.activeElement)) || !document.activeElement) {
            !disableAutoHideOnSubmit && dispatch(hideDialog());
            onSubmit?.();
        }
    }, [onSubmit]);
    return (React.createElement(BaseDialog, { className: className, description: description, disableBackdropClose: disableBackdropClose, disableEnter: disableEnter, disableEscape: disableEscape, onClose: onClose, size: size, submit: submit, testId: testId, title: title, titleKey: titleKey },
        React.createElement("div", { className: classes.header },
            React.createElement("h1", { className: classes.title, id: "dialog-title" }, title ?? t(titleKey ?? "")),
            !hideCloseButton && (React.createElement(ClickableIcon, { accessibilityLabel: t("dialog.accessibilityLabel.close"), icon: IconCloseLarge, id: "modal-header-close-button", onClick: onClose }))),
        React.createElement("div", { className: classes.content, "data-autofocus-inside": "true" }, children),
        React.createElement("div", { className: classes.footer, "data-autofocus-inside": "true" },
            !back.hidden && (React.createElement(Button, { accessibilityLabel: t(back.translationKey ?? ""), labelKey: back.translationKey, 
                // eslint-disable-next-line react/jsx-handler-names
                onClick: back.onClick, type: "secondary" })),
            !cancel.hidden && (React.createElement(Button, { accessibilityLabel: t(cancel.translationKey ?? ""), labelKey: cancel.translationKey, onClick: onClose, type: "tertiary" })),
            !ok.hidden && (React.createElement(Button, { accessibilityLabel: t(ok.translationKey ?? ""), disabled: ok.disabled, id: "modal-dialog-ok-button", isSubmit: true, labelKey: ok.translationKey, ...(!ok.disabled && { onClick: submit }) })))));
};
export default Dialog;
